import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styles from './Button.module.css';

type Variant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'light'
  | 'outlineLight'
  | 'accentOutline';
type Size = 'md' | 'lg';

interface BaseProps {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  className?: string;
}

interface ButtonAsButton
  extends BaseProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> {
  to?: undefined;
  href?: undefined;
}

interface ButtonAsLink extends BaseProps {
  /** Internal route — renders a react-router Link. */
  to: string;
  href?: undefined;
}

interface ButtonAsAnchor extends BaseProps {
  /** External URL — renders an anchor. */
  href: string;
  to?: undefined;
}

type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor;

/** Polymorphic button that renders as <button>, router <Link>, or <a>. */
export function Button(props: ButtonProps) {
  const {
    children,
    variant = 'primary',
    size = 'md',
    className,
    ...rest
  } = props;

  const classes = [styles.btn, styles[variant], styles[size], className]
    .filter(Boolean)
    .join(' ');

  if ('to' in props && props.to) {
    const { to } = props;
    return (
      <Link to={to} className={classes}>
        {children}
      </Link>
    );
  }

  if ('href' in props && props.href) {
    const { href } = props;
    return (
      <a
        href={href}
        className={classes}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }

  return (
    <button
      className={classes}
      {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {children}
    </button>
  );
}
